import React, { Component, useState, useEffect } from "react";
import { connect } from "react-redux";
import AsyncSwitch from "../AsyncSwitch";
import { LabeledDropDownSelect } from "../DropDownSelect";
import AdminOnlyWarning from "../Settings/AdminOnlyWarning";
import {
	getMandatoryProfilingSettings,
	updateMandatoryProfilingSetting,
} from "../../actions/mandatoryProfilingActions";
import {
	getProfilingRequirementsDescription,
	getFileTypeExclusionsDescription,
	getFileTypeProfilingDescription,
} from "./mandatoryProfilingDescriptions";

// Helper component for managing mandatory field switches
const MandatoryFieldSwitch = ({
	fieldKey,
	label,
	settingsObject,
	updateSettings,
	canEdit,
	mandatoryProfilingEnabled,
	loading,
}) => {
	return (
		<AsyncSwitch
			label={label}
			name={fieldKey}
			switchValue={settingsObject[fieldKey]}
			disabled={!canEdit || !mandatoryProfilingEnabled}
			loading={loading}
			updateSetting={(newValue) => {
				return updateSettings(fieldKey, newValue);
			}}
		/>
	);
};

// Helper component for styled description box
const DescriptionBox = ({ children }) => {
	return (
		<div
			style={{
				padding: 15,
				backgroundColor: "#f8f9fa",
				border: "1px solid #dee2e6",
				borderRadius: 4,
				marginTop: 24,
			}}
		>
			{children}
		</div>
	);
};

// Helper component for displaying a single file extension
const ExtensionTag = ({ extension }) => {
	return (
		<span
			style={{
				fontFamily: "monospace",
				fontSize: 12,
				padding: "2px 6px",
				border: "1px solid #dee2e6",
				borderRadius: 3,
				backgroundColor: "#f8f9fa",
			}}
		>
			{extension}
		</span>
	);
};

// Helper component for displaying a group of extensions for a category
const ExtensionCategory = ({ category }) => {
	return (
		<div
			style={{
				marginBottom: 8,
				paddingLeft: 15,
			}}
		>
			<div
				style={{
					fontSize: 14,
					fontWeight: 500,
					marginBottom: 4,
				}}
			>
				{category.label}:
			</div>
			<div
				style={{
					display: "flex",
					flexWrap: "wrap",
					gap: 4,
				}}
			>
				{category.extensions.map((ext, extIndex) => (
					<ExtensionTag key={extIndex} extension={ext} />
				))}
			</div>
		</div>
	);
};

const MandatoryProfilingSettings = (props) => {
	const [loading, setLoading] = useState(true);
	const [updatingFileTypes, setUpdatingFileTypes] = useState(false);
	const [settingsObject, setSettingsObject] = useState({
		mandatoryProfilingEnabled: false,
		makeDocumentTypeRequired: false,
		makeDocumentStatusRequired: false,
		makeDocumentTagsRequired: false,
	});

	const [fileTypeOptions, setFileTypeOptions] = useState([]);
	const [excludedFileTypes, setExcludedFileTypes] = useState([]);

	useEffect(() => {
		getMandatoryProfilingSettings()
			.then((data) => {
				setSettingsObject({
					mandatoryProfilingEnabled: !!data.mandatoryProfilingEnabled,
					makeDocumentTypeRequired:
						!!data.requiredFields?.documentType,
					makeDocumentStatusRequired:
						!!data.requiredFields?.documentStatus,
					makeDocumentTagsRequired:
						!!data.requiredFields?.documentTags,
				});

				// Set file type options from backend
				if (data.ignoredExtensionOptions) {
					setFileTypeOptions(data.ignoredExtensionOptions);
				}

				// Set excluded file types from backend
				// Convert array of values to array of objects for dropdown
				if (data.excludedFileTypes && data.ignoredExtensionOptions) {
					const selectedOptions = data.excludedFileTypes.map(
						(value) => {
							const option = data.ignoredExtensionOptions.find(
								(opt) => opt.value === value
							);
							return option || { value, label: value };
						}
					);
					setExcludedFileTypes(selectedOptions);
				} else if (data.ignoredExtensionOptions) {
					// If no excluded file types, set empty array
					setExcludedFileTypes([]);
				}

				setLoading(false);
			})
			.catch((error) => {
				console.error(
					"Failed to load mandatory profiling settings:",
					error
				);
				setLoading(false);
			});
	}, []);

	const updateSettings = (fieldKey, newValue) => {
		const updatedSettings = {
			...settingsObject,
			[fieldKey]: newValue,
		};

		// Format the settings for the API
		const apiPayload = {
			mandatoryProfilingEnabled:
				updatedSettings.mandatoryProfilingEnabled,
			requiredFields: {
				documentType: updatedSettings.makeDocumentTypeRequired,
				documentStatus: updatedSettings.makeDocumentStatusRequired,
				documentTags: updatedSettings.makeDocumentTagsRequired,
			},
			// Convert dropdown objects to array of values for backend
			excludedFileTypes: excludedFileTypes.map((opt) => opt.value),
		};

		return updateMandatoryProfilingSetting(apiPayload)
			.then(() => {
				setSettingsObject(updatedSettings);
			})
			.catch((e) => {
				console.error(
					"Failed to update mandatory profiling settings:",
					e
				);
				throw e; // Re-throw to let AsyncSwitch handle the error
			});
	};

	const handleFileTypeChange = (name, selectedOptions) => {
		if (!props.canEditMandatoryProfilingSettings || updatingFileTypes)
			return;

		// Store current state in a variable that won't be affected by state updates
		const previousExcludedFileTypes = [...excludedFileTypes];
		const newExcludedTypes = selectedOptions || [];

		// Set loading state and optimistically update the UI
		setUpdatingFileTypes(true);
		setExcludedFileTypes(newExcludedTypes);

		// Convert dropdown objects to array of values for backend
		const excludedValues = newExcludedTypes.map((opt) =>
			opt && opt.value ? opt.value : opt
		);

		// Send update to backend immediately with all current settings
		const apiPayload = {
			mandatoryProfilingEnabled: settingsObject.mandatoryProfilingEnabled,
			requiredFields: {
				documentType: settingsObject.makeDocumentTypeRequired,
				documentStatus: settingsObject.makeDocumentStatusRequired,
				documentTags: settingsObject.makeDocumentTagsRequired,
			},
			excludedFileTypes: excludedValues,
		};

		return updateMandatoryProfilingSetting(apiPayload)
			.catch((e) => {
				console.error("Failed to update excluded file types:", e);
				// Revert to the previous state before the update
				setExcludedFileTypes(previousExcludedFileTypes);
			})
			.finally(() => {
				setUpdatingFileTypes(false);
			});
	};

	const fileTypeExclusionsDescription = getFileTypeProfilingDescription(
		settingsObject.mandatoryProfilingEnabled,
		excludedFileTypes
	);
	const exclusions = getFileTypeExclusionsDescription(
		excludedFileTypes,
		fileTypeOptions
	);

	return (
		<div style={{ width: 700, paddingTop: 20, paddingLeft: 15 }}>
			{props.parentComponentIsLoading || loading ? null : (
				<div style={{ marginBottom: 40 }}>
					<AdminOnlyWarning
						userCanEditSettings={
							props.canEditMandatoryProfilingSettings
						}
					/>

					<div>
						<AsyncSwitch
							label="Prompt Users to Profile New Documents"
							switchValue={
								settingsObject.mandatoryProfilingEnabled
							}
							disabled={!props.canEditMandatoryProfilingSettings}
							loading={loading}
							updateSetting={(newValue) => {
								return updateSettings(
									"mandatoryProfilingEnabled",
									newValue
								);
							}}
						/>
						<div>
							<MandatoryFieldSwitch
								fieldKey="makeDocumentTypeRequired"
								label={
									<span>
										Make <b>Document Type</b> a Required
										Field
									</span>
								}
								settingsObject={settingsObject}
								updateSettings={updateSettings}
								canEdit={
									props.canEditMandatoryProfilingSettings
								}
								mandatoryProfilingEnabled={
									settingsObject.mandatoryProfilingEnabled
								}
								loading={loading}
							/>

							<MandatoryFieldSwitch
								fieldKey="makeDocumentStatusRequired"
								label={
									<span>
										Make <b>Document Status</b> a Required
										Field
									</span>
								}
								settingsObject={settingsObject}
								updateSettings={updateSettings}
								canEdit={
									props.canEditMandatoryProfilingSettings
								}
								mandatoryProfilingEnabled={
									settingsObject.mandatoryProfilingEnabled
								}
								loading={loading}
							/>

							<MandatoryFieldSwitch
								fieldKey="makeDocumentTagsRequired"
								label={
									<span>
										Make <b>Document Tags</b> a Required
										Field
									</span>
								}
								settingsObject={settingsObject}
								updateSettings={updateSettings}
								canEdit={
									props.canEditMandatoryProfilingSettings
								}
								mandatoryProfilingEnabled={
									settingsObject.mandatoryProfilingEnabled
								}
								loading={loading}
							/>
						</div>
					</div>
					{settingsObject.mandatoryProfilingEnabled ? (
						<div>
							<h3 style={{ fontSize: 18, marginBottom: 16 }}>
								Exclude Specific File Types from Profiling
								Requirements
							</h3>
							<LabeledDropDownSelect
								label=""
								name="excludedFileTypes"
								options={fileTypeOptions}
								value={excludedFileTypes}
								handler={handleFileTypeChange}
								isMulti={true}
								placeholder="Select file types to exclude..."
								isDisabled={
									!props.canEditMandatoryProfilingSettings ||
									!settingsObject.mandatoryProfilingEnabled ||
									updatingFileTypes
								}
								isClearable={true}
							/>
						</div>
					) : null}

					<DescriptionBox>
						<div>
							{getProfilingRequirementsDescription({
								mandatoryProfilingEnabled:
									settingsObject.mandatoryProfilingEnabled,
								requiredFields: {
									documentType:
										settingsObject.makeDocumentTypeRequired,
									documentStatus:
										settingsObject.makeDocumentStatusRequired,
									documentTags:
										settingsObject.makeDocumentTagsRequired,
								},
							})}
						</div>

						{settingsObject.mandatoryProfilingEnabled ? (
							<div style={{ marginTop: 24 }}>
								<div style={{ marginBottom: 12 }}>
									{fileTypeExclusionsDescription}
								</div>
								<div>
									{exclusions && exclusions.length
										? exclusions.map((category, index) => (
												<ExtensionCategory
													key={index}
													category={category}
												/>
											))
										: null}
								</div>
							</div>
						) : null}
					</DescriptionBox>
				</div>
			)}
		</div>
	);
};

class MandatoryProfilingSettingsWrapper extends Component {
	render() {
		return (
			<MandatoryProfilingSettings
				canEditMandatoryProfilingSettings={
					this.props.canEditMandatoryProfilingSettings
				}
				parentComponentIsLoading={this.props.loading}
			/>
		);
	}
}

const mapStateToProps = (state) => ({
	canEditMandatoryProfilingSettings: state.user.userRights
		? state.user.userRights.UPDATE_MANDATORY_PROFILING_SETTINGS
		: false,
	loading: state.user.loading,
});

export default connect(mapStateToProps, {})(MandatoryProfilingSettingsWrapper);
